import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  type StatusTone,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { useHubEmbedded } from "@/modules/hub-kit/HubKit";
import {
  IDENTITY_FIX_LABELS,
  createIdentityVerificationApi,
  type IdentityDetailResponse,
  type BirthdayChangeDto,
  type IdentityAccessDto,
  type IdentityDocumentDto,
  type IdentityFixKey,
  type IdentityVerificationApi,
  type IdentityVerificationDto,
} from "../identity-api";

/**
 * Upgrade Account applications, reviewed from Creator Center.
 *
 * ADR-004: photos stay in the private bucket. Opening this page asks the API
 * for a two-minute link per photo (the API writes an audit row first). The
 * link is shown here and dropped on expiry. It is never put in the address
 * bar, stored, or cached.
 */

const STATUS_LABEL: Record<string, string> = {
  submitted: "Awaiting review",
  under_review: "Under review",
  more_information_required: "Changes requested",
  verified: "Accepted",
  accepted: "Accepted",
  rejected: "Denied",
  missing: "Not uploaded",
};

const FIX_KEYS: IdentityFixKey[] = ["id_front", "id_back", "id_selfie", "legal_name", "birthdate", "id_type"];
const GUARDIAN_FIX_KEYS: IdentityFixKey[] = ["guardian_name", "guardian_id", "guardian_selfie"];

type Deciding = null | "accept" | "deny" | "request-modification";

function tone(status: string): StatusTone {
  if (status === "verified" || status === "accepted") return "success";
  if (status === "rejected") return "danger";
  if (status === "missing") return "neutral";
  return "warning";
}

function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function useIdentityApi(): IdentityVerificationApi {
  const client = useApiClient();
  return useMemo(() => createIdentityVerificationApi(client), [client]);
}

function allowanceText(a: { limit: number | null; granted: number }): string {
  return a.limit === null
    ? `${a.granted} free Verified upgrades granted · no cap set`
    : `${a.granted} of ${a.limit} free Verified upgrades granted`;
}

/* ── Queue ────────────────────────────────────────────────────────────── */

export function IdentityVerificationQueuePage() {
  const embedded = useHubEmbedded();
  usePageBreadcrumbs(
    embedded
      ? embedded.crumbs
      : [
          { label: "HUDDL HQ", to: "/app" },
          { label: "Creator Center", to: "/app/creators" },
          { label: "Applications" },
        ],
  );
  const api = useIdentityApi();
  const navigate = useNavigate();
  const [scope, setScope] = useState<"awaiting" | "decided">("awaiting");
  const [rows, setRows] = useState<IdentityVerificationDto[] | null>(null);
  const [allowance, setAllowance] = useState<{ limit: number | null; granted: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    try {
      const res = await api.list(scope);
      setRows(res.verifications);
      setAllowance(res.free_allowance);
      setDenied(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setDenied(true);
      else setError(apiErrorMessage(err));
      setRows([]);
    }
  }, [api, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap={16}>
      <SectionHeader
        title="Applications"
        description="Upgrade Account submissions from the HUDDL app. Open one to read what they declared and look at the ID photos before you decide."
        actions={
          <Flex gap={8}>
            <Button variant={scope === "awaiting" ? "primary" : "secondary"} onClick={() => setScope("awaiting")}>
              Awaiting review
            </Button>
            <Button variant={scope === "decided" ? "primary" : "secondary"} onClick={() => setScope("decided")}>
              Decided
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />
      {allowance ? (
        <Text role="small" color="secondary" as="p">
          {allowanceText(allowance)}. Internal only — players are never shown this number.
        </Text>
      ) : null}
      <Surface>
        {rows === null ? (
          <LoadingState message="Loading applications…" />
        ) : denied ? (
          <EmptyState
            title="You cannot review applications"
            description="This needs the verification view permission on your access assignment."
            reason="scope"
          />
        ) : error ? (
          <EmptyState
            title="Applications unavailable"
            description={error}
            reason="scope"
            action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No applications found"
            description={
              scope === "awaiting"
                ? "Players submit from Profile in the HUDDL app. New submissions appear here."
                : "Accepted, denied and sent-back applications appear here."
            }
            reason="none"
          />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Applicant</Th>
                <Th>ID</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
                <Th> </Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <Stack gap={4}>
                      <Text role="body" as="span">{r.display_name ?? "—"}</Text>
                      <Text role="caption" color="muted" as="span">
                        {r.handle ? `@${r.handle}` : r.user_id}
                      </Text>
                    </Stack>
                  </Td>
                  <Td>{r.id_type_label ?? "—"}</Td>
                  <Td>
                    <StatusChip tone={tone(r.status)}>{STATUS_LABEL[r.status] ?? r.status}</StatusChip>
                  </Td>
                  <Td>{when(r.submitted_at ?? r.requested_at)}</Td>
                  <Td>
                    <Button size="sm" variant="secondary" onClick={() => void navigate(`/app/creators/applications/${r.id}`)}>
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Surface>
    </Stack>
  );
}

/* ── Detail ───────────────────────────────────────────────────────────── */

export function IdentityVerificationDetailPage() {
  const params = useParams();
  const verificationId = params.verificationId ?? params.applicationId ?? "";
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Creator Center", to: "/app/creators?tab=applications" },
    { label: "Application" },
  ]);
  const api = useIdentityApi();
  const toast = useToast();
  const [data, setData] = useState<IdentityDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<Deciding>(null);
  const [note, setNote] = useState("");
  const [fixes, setFixes] = useState<IdentityFixKey[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.detail(verificationId));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? `You cannot review this application. ${apiErrorMessage(err)}`
          : apiErrorMessage(err),
      );
    }
  }, [api, verificationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <EmptyState
        title="Application unavailable"
        description={error}
        reason="scope"
        action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>}
      />
    );
  }
  if (!data) return <LoadingState message="Loading application…" />;

  const v = data.verification;
  const open = v.status === "submitted" || v.status === "under_review";
  const allReadable = data.documents.every((d) => d.file_available);
  const needsNote = choice === "deny" || choice === "request-modification";
  const canConfirm =
    choice !== null &&
    (!needsNote || note.trim().length >= 3) &&
    (choice !== "request-modification" || fixes.length > 0) &&
    (choice !== "accept" || (allReadable && v.birthdate_matches !== false));

  const decide = async () => {
    if (!choice || !canConfirm) return;
    setBusy(true);
    try {
      const res = await api.decide(v.id, choice, {
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(choice === "request-modification" ? { fixes } : {}),
      });
      toast.push({
        title:
          choice === "accept"
            ? res.verification.free_upgrade_granted
              ? "Accepted — Verified tier granted free"
              : "Accepted — badge granted (free allowance used up)"
            : choice === "deny"
              ? "Denied"
              : "Sent back for changes",
        tone: choice === "accept" ? "success" : "warning",
      });
      setChoice(null);
      setNote("");
      setFixes([]);
      await load();
    } catch (err) {
      toast.push({ title: "Decision not saved", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={16}>
      <SectionHeader
        title={v.display_name ?? v.legal_name ?? "Application"}
        description={
          <span>
            {v.handle ? `@${v.handle} · ` : null}
            <Link to={`/app/users/${v.user_id}`}>Open the user record</Link>
          </span>
        }
        actions={<StatusChip tone={tone(v.status)}>{STATUS_LABEL[v.status] ?? v.status}</StatusChip>}
      />

      <Surface>
        <Stack gap={8}>
          <Text role="h2" as="h2">What they submitted</Text>
          <Table>
            <Tbody>
              <Tr><Th>Legal name</Th><Td>{v.legal_name ?? "—"}</Td></Tr>
              <Tr><Th>Birthdate</Th><Td>{v.birthdate ?? "—"}</Td></Tr>
              <Tr>
                <Th>Account birthday</Th>
                <Td>
                  <Flex gap={8} style={{ alignItems: "center" }}>
                    <span>{v.account_birthdate ?? "Not set"}</span>
                    <StatusChip tone={v.birthdate_matches ? "success" : "danger"}>
                      {v.birthdate_matches ? "Matches" : "Does not match"}
                    </StatusChip>
                  </Flex>
                </Td>
              </Tr>
              <Tr><Th>ID</Th><Td>{v.id_type_label ?? "—"}</Td></Tr>
              {v.guardian_name ? (
                <>
                  <Tr><Th>Parent or guardian</Th><Td>{v.guardian_name}</Td></Tr>
                  <Tr><Th>Relationship</Th><Td>{v.guardian_relationship_label ?? v.guardian_relationship ?? "—"}</Td></Tr>
                </>
              ) : null}
              <Tr><Th>Submitted</Th><Td>{when(v.submitted_at)}</Td></Tr>
              {v.review_note ? <Tr><Th>Last note to the player</Th><Td>{v.review_note}</Td></Tr> : null}
              {v.required_fixes.length > 0 ? (
                <Tr><Th>Asked to redo</Th><Td>{v.required_fixes.map((f) => IDENTITY_FIX_LABELS[f]).join(", ")}</Td></Tr>
              ) : null}
            </Tbody>
          </Table>
          <Text role="caption" color="muted" as="p">
            The name and birthdate should match the ID, and the birthdate must equal the account birthday or Accept is refused.
            The selfie should be the same person holding that ID.
            {v.guardian_name ? " Under 18: the guardian's ID and selfie must show the named person." : ""}
            {" "}{allowanceText(data.free_allowance)}.
          </Text>
        </Stack>
      </Surface>

      <Surface>
        <PrivatePhotos
          documents={data.documents}
          open={async (documentId) => (await api.openDocument(v.id, documentId)).access}
        />
      </Surface>

      {data.history.length > 1 ? (
        <Surface>
          <Stack gap={8}>
            <Text role="h2" as="h2">Earlier attempts</Text>
            <Table>
              <Thead><Tr><Th>Status</Th><Th>Submitted</Th><Th>Decided</Th><Th>Note</Th></Tr></Thead>
              <Tbody>
                {data.history.filter((h) => h.id !== v.id).map((h) => (
                  <Tr key={h.id}>
                    <Td><StatusChip tone={tone(h.status)}>{STATUS_LABEL[h.status] ?? h.status}</StatusChip></Td>
                    <Td>{when(h.requested_at)}</Td>
                    <Td>{when(h.reviewed_at)}</Td>
                    <Td>{h.decision_reason ?? "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Stack>
        </Surface>
      ) : null}

      <Surface>
        <Stack gap={12}>
          <Text role="h2" as="h2">Decision</Text>
          {open ? (
            <>
              <Text role="small" color="secondary" as="p">
                Look at the name, birthdate and photos above, then choose one outcome. The player reads a deny or a change request in the app.
              </Text>
              <Flex gap={8} wrap>
                <Button variant={choice === "accept" ? "primary" : "secondary"} onClick={() => setChoice("accept")}>
                  Accept
                </Button>
                <Button variant={choice === "request-modification" ? "primary" : "secondary"} onClick={() => setChoice("request-modification")}>
                  Ask for changes
                </Button>
                <Button variant={choice === "deny" ? "danger" : "secondary"} onClick={() => setChoice("deny")}>
                  Deny
                </Button>
              </Flex>
              {choice === "accept" ? (
                <Stack gap={8}>
                  <Text role="small" as="p">
                    The player gets the verified badge
                    {data.free_allowance.limit === null || data.free_allowance.granted < data.free_allowance.limit
                      ? " and the Verified tier, free"
                      : ""}
                    . They are notified in the app.
                  </Text>
                  {!allReadable ? (
                    <Alert tone="warning" title="A photo is missing">
                      Accept stays off until every photo above is on file.
                    </Alert>
                  ) : null}
                  {v.birthdate_matches === false ? (
                    <Alert tone="warning" title="The birthdates differ">
                      The application must show the account birthday. Ask for changes (Birthdate), or deny.
                    </Alert>
                  ) : null}
                  <Textarea
                    label="Note (optional, internal)"
                    value={note}
                    maxLength={1000}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Stack>
              ) : null}
              {choice === "request-modification" ? (
                <Stack gap={8}>
                  <Text role="small" as="p">
                    Tick what they must redo. Ticked photos are rejected with your note. They retake only those and submit again.
                  </Text>
                  {(v.guardian_name ? [...FIX_KEYS, ...GUARDIAN_FIX_KEYS] : FIX_KEYS).map((k) => (
                    <Checkbox
                      key={k}
                      id={`fix-${k}`}
                      label={IDENTITY_FIX_LABELS[k]}
                      checked={fixes.includes(k)}
                      onChange={(e) =>
                        setFixes((prev) => (e.target.checked ? [...prev, k] : prev.filter((f) => f !== k)))
                      }
                    />
                  ))}
                  <Textarea
                    label="Note to the player (they read this)"
                    value={note}
                    maxLength={1000}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Stack>
              ) : null}
              {choice === "deny" ? (
                <Stack gap={8}>
                  <Text role="small" as="p">
                    A denial closes this attempt. The player reads your reason in the app.
                  </Text>
                  <Textarea
                    label="Reason the player reads"
                    value={note}
                    maxLength={1000}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Stack>
              ) : null}
              {choice ? (
                <Flex>
                  <Button
                    variant={choice === "deny" ? "danger" : "primary"}
                    loading={busy}
                    disabled={!canConfirm}
                    onClick={() => void decide()}
                  >
                    {choice === "accept" ? "Accept application" : choice === "deny" ? "Deny application" : "Send back for changes"}
                  </Button>
                </Flex>
              ) : null}
            </>
          ) : (
            <Text role="small" color="secondary" as="p">
              This application is already {STATUS_LABEL[v.status]?.toLowerCase() ?? v.status}.
              {v.review_note ? ` Last note: ${v.review_note}` : ""}
            </Text>
          )}
        </Stack>
      </Surface>
    </Stack>
  );
}

/** The audited two-minute photo links, shared by applications and birthday changes. */
function PrivatePhotos({
  documents,
  open,
}: {
  documents: IdentityDocumentDto[];
  open: (documentId: string) => Promise<IdentityAccessDto>;
}) {
  const [shots, setShots] = useState<Record<string, { url: string; until: number } | { error: string }>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const next: Record<string, { url: string; until: number } | { error: string }> = {};
    await Promise.all(documents.map(async (d) => {
      if (!d.id || !d.file_available) {
        next[d.requirement_key] = { error: "Not uploaded" };
        return;
      }
      try {
        const access = await open(d.id);
        next[d.requirement_key] = { url: access.url, until: Date.parse(access.expires_at) };
      } catch (err) {
        next[d.requirement_key] = { error: apiErrorMessage(err) };
      }
    }));
    setShots(next);
    setLoading(false);
    // `open` is a fresh closure each render; the documents decide when to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap={12}>
      <Flex gap={8} style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Stack gap={4}>
          <Text role="h2" as="h2">Photos</Text>
          <Text role="small" color="secondary" as="p">
            Private. Opening this application is logged. Each photo link expires in two minutes.
          </Text>
        </Stack>
        <Button size="sm" variant="secondary" onClick={() => void load()}>Refresh photos</Button>
      </Flex>
      {loading ? <LoadingState message="Loading photos…" /> : null}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        {documents.map((d) => {
          const shot = shots[d.requirement_key];
          const url = shot && "url" in shot ? shot.url : null;
          const expired = shot && "url" in shot && shot.until <= Date.now();
          return (
            <Stack key={d.requirement_key} gap={8}>
              <Flex gap={8} style={{ alignItems: "center" }}>
                <Text role="body" as="span">{d.label}</Text>
                <StatusChip tone={tone(d.status)}>{STATUS_LABEL[d.status] ?? d.status}</StatusChip>
              </Flex>
              <div style={{
                minHeight: 180,
                borderRadius: 12,
                background: "var(--color-surface-sunken, #f4f6f8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}>
                {url && !expired ? (
                  <img
                    src={url}
                    alt={d.label}
                    referrerPolicy="no-referrer"
                    style={{ width: "100%", maxHeight: 360, objectFit: "contain" }}
                  />
                ) : (
                  <Text role="small" color="secondary" as="span">
                    {shot && "error" in shot ? shot.error : expired ? "Link expired" : "Waiting"}
                  </Text>
                )}
              </div>
            </Stack>
          );
        })}
      </div>
    </Stack>
  );
}

/* ── Birthday changes (i136) ──────────────────────────────────────────── */

const BIRTHDAY_STATUS: Record<string, { label: string; tone: StatusTone }> = {
  pending: { label: "Awaiting review", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  denied: { label: "Denied", tone: "danger" },
};

/**
 * Players change their locked birthday with a document and a selfie. Approving
 * writes the new date; the age rules and forced privacy follow from it.
 */
export function BirthdayChangeQueuePage() {
  const api = useIdentityApi();
  const navigate = useNavigate();
  const [scope, setScope] = useState<"awaiting" | "decided">("awaiting");
  const [rows, setRows] = useState<BirthdayChangeDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    try {
      setRows((await api.listBirthdayChanges(scope)).requests);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
    }
  }, [api, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap={16}>
      <SectionHeader
        title="Birthday changes"
        description="Birthdays are locked after sign-up. A player asks to change theirs with a document and a selfie holding it."
        actions={
          <Flex gap={8}>
            <Button variant={scope === "awaiting" ? "primary" : "secondary"} onClick={() => setScope("awaiting")}>
              Awaiting review
            </Button>
            <Button variant={scope === "decided" ? "primary" : "secondary"} onClick={() => setScope("decided")}>
              Decided
            </Button>
          </Flex>
        }
      />
      <Surface>
        {rows === null ? (
          <LoadingState message="Loading requests…" />
        ) : error ? (
          <EmptyState
            title="Requests unavailable"
            description={error}
            reason="scope"
            action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No birthday changes"
            description={scope === "awaiting" ? "Players ask from My Account in the HUDDL app." : "Decided requests appear here."}
            reason="none"
          />
        ) : (
          <Table>
            <Thead>
              <Tr><Th>Player</Th><Th>Status</Th><Th>Asked</Th><Th> </Th></Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <Stack gap={4}>
                      <Text role="body" as="span">{r.display_name ?? "—"}</Text>
                      <Text role="caption" color="muted" as="span">{r.handle ? `@${r.handle}` : r.user_id}</Text>
                    </Stack>
                  </Td>
                  <Td>
                    <StatusChip tone={BIRTHDAY_STATUS[r.status]?.tone ?? "neutral"}>
                      {BIRTHDAY_STATUS[r.status]?.label ?? r.status}
                    </StatusChip>
                  </Td>
                  <Td>{when(r.created_at)}</Td>
                  <Td>
                    <Button size="sm" variant="secondary" onClick={() => void navigate(`/app/creators/birthday-changes/${r.id}`)}>
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Surface>
    </Stack>
  );
}

export function BirthdayChangeDetailPage() {
  const { requestId = "" } = useParams();
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Creator Center", to: "/app/creators?tab=applications&sub=birthdays" },
    { label: "Birthday change" },
  ]);
  const api = useIdentityApi();
  const toast = useToast();
  const [data, setData] = useState<{ request: BirthdayChangeDto; documents: IdentityDocumentDto[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<null | "approve" | "deny">(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.birthdayChange(requestId));
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }, [api, requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <EmptyState
        title="Request unavailable"
        description={error}
        reason="scope"
        action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>}
      />
    );
  }
  if (!data) return <LoadingState message="Loading request…" />;

  const r = data.request;
  const open = r.status === "pending";
  const allReadable = data.documents.length === 2 && data.documents.every((d) => d.file_available);
  const canConfirm = choice === "approve" ? allReadable : choice === "deny" ? note.trim().length >= 3 : false;

  const decide = async () => {
    if (!choice || !canConfirm) return;
    setBusy(true);
    try {
      await api.decideBirthdayChange(r.id, choice, note.trim() ? { note: note.trim() } : {});
      toast.push({
        title: choice === "approve" ? "Birthday updated" : "Request denied",
        tone: choice === "approve" ? "success" : "warning",
      });
      setChoice(null);
      setNote("");
      await load();
    } catch (err) {
      toast.push({ title: "Decision not saved", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap={16}>
      <SectionHeader
        title="Birthday change"
        description={<Link to={`/app/users/${r.user_id}`}>Open the user record</Link>}
        actions={
          <StatusChip tone={BIRTHDAY_STATUS[r.status]?.tone ?? "neutral"}>
            {BIRTHDAY_STATUS[r.status]?.label ?? r.status}
          </StatusChip>
        }
      />
      <Surface>
        <Table>
          <Tbody>
            <Tr><Th>Birthday now</Th><Td>{r.previous_birthdate ?? "Not set"}</Td></Tr>
            <Tr><Th>Asked to change to</Th><Td>{r.requested_birthdate ?? "—"}</Td></Tr>
            <Tr><Th>Asked</Th><Td>{when(r.created_at)}</Td></Tr>
            {r.review_note ? <Tr><Th>Note</Th><Td>{r.review_note}</Td></Tr> : null}
          </Tbody>
        </Table>
      </Surface>
      <Surface>
        <PrivatePhotos
          documents={data.documents}
          open={async (documentId) => (await api.openBirthdayDocument(r.id, documentId)).access}
        />
      </Surface>
      <Surface>
        <Stack gap={12}>
          <Text role="h2" as="h2">Decision</Text>
          {open ? (
            <>
              <Text role="small" color="secondary" as="p">
                The document must show the requested date, and the selfie must be the player holding that same document.
              </Text>
              <Flex gap={8}>
                <Button variant={choice === "approve" ? "primary" : "secondary"} onClick={() => setChoice("approve")}>Approve</Button>
                <Button variant={choice === "deny" ? "danger" : "secondary"} onClick={() => setChoice("deny")}>Deny</Button>
              </Flex>
              {choice === "approve" && !allReadable ? (
                <Alert tone="warning" title="A photo is missing">Approve stays off until both photos are on file.</Alert>
              ) : null}
              {choice ? (
                <Textarea
                  label={choice === "deny" ? "Reason the player reads" : "Note (optional)"}
                  value={note}
                  maxLength={1000}
                  onChange={(e) => setNote(e.target.value)}
                />
              ) : null}
              {choice ? (
                <Flex>
                  <Button
                    variant={choice === "deny" ? "danger" : "primary"}
                    loading={busy}
                    disabled={!canConfirm}
                    onClick={() => void decide()}
                  >
                    {choice === "approve" ? "Approve and update the birthday" : "Deny request"}
                  </Button>
                </Flex>
              ) : null}
            </>
          ) : (
            <Text role="small" color="secondary" as="p">
              This request was already {BIRTHDAY_STATUS[r.status]?.label.toLowerCase() ?? r.status}.
            </Text>
          )}
        </Stack>
      </Surface>
    </Stack>
  );
}
